import React, { useMemo } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useEffectiveAccess } from '../../agenticsaas/contexts/EffectiveAccessContext';
import Header from '../../shared/components/layout/Header';
import Sidebar from './Sidebar';
import AccessGuard from '../auth/AccessGuard';
import { DEFAULT_HOME_ROUTE } from '../../config/navAccessMap';
import ServiceFrame from '../ServiceFrame';
import LandingDashboard, {
  ExecutiveDashboardPage,
  OperationalDashboardPage,
  FinanceDashboardPage,
} from '../../pages/LandingDashboard';
import RequireDashboardView from '../../pages/dashboard/components/RequireDashboardView';
import Profile from '../../pages/Profile';
import { DASHBOARD_VIEWS } from '../../utils/dashboardPreferences';
import Settings from '../../pages/Settings';
import WorkQueue from '../../pages/WorkQueue/WorkQueue';
import Notifications from '../../pages/Notifications';
import TaskManager from '../../pages/TaskManager/TaskManager';
import ActivityLog from '../../pages/ActivityLog/ActivityLog';
import Analytics from '../../pages/Analytics/Analytics';
import Reporting from '../../pages/Reporting/Reporting';
import ContactUs from '../../pages/ContactUs';
import Help from '../../pages/Help';
import PatientPortal from '../../pages/PatientPortal';
import SubscriptionManagement from '../../agenticsaas/components/subscription/SubscriptionManagement';
import TeamManagement from '../../agenticsaas/components/team/TeamManagement';
import ProductTourChecklist from '../onboarding/ProductTourChecklist';
import DemoEnvironmentBanner from '../onboarding/DemoEnvironmentBanner';
import SubscriptionBanner from '../onboarding/SubscriptionBanner';
import { PlatformKaiHost } from '@saas-foundation/shared-frontend/kai';

const DashboardShell = ({ children }) => (
  <div className="py-6 px-4 sm:px-6 lg:px-8 overflow-y-auto flex-1 min-h-0" style={{ backgroundColor: '#f9fafb' }}>
    {children}
  </div>
);

export default function ProtectedAppLayout({ navigationItems, servicePorts }) {
  const { filterNavigationItems } = useEffectiveAccess();
  const visibleNav = useMemo(
    () => filterNavigationItems(navigationItems),
    [navigationItems, filterNavigationItems]
  );
  const organizationId = useMemo(() => {
    if (typeof window === 'undefined') return null;
    return (
      localStorage.getItem('selectedOrganizationId') ||
      localStorage.getItem('organizationId') ||
      localStorage.getItem('organization_id') ||
      null
    );
  }, []);

  return (
    <PlatformKaiHost defaultServiceKey="platform">
    <div className="min-h-screen bg-gray-100 flex">
      <Sidebar navigationItems={visibleNav} />
      <div className="flex-1 flex flex-col overflow-hidden">
        <Header title="Agentic End-to-End RCM" />
        <DemoEnvironmentBanner organizationId={organizationId} />
        <SubscriptionBanner />
        <main
          className="flex-1 flex flex-col min-h-0 overflow-hidden bg-gray-50"
          style={{ height: 'calc(100vh - 64px)', minHeight: 0 }}
        >
          <AccessGuard>
            <Routes>
              <Route path="/" element={<DashboardShell><LandingDashboard /></DashboardShell>} />
              <Route path="/dashboard" element={<DashboardShell><LandingDashboard /></DashboardShell>} />
              <Route path="/dashboard/executive" element={<DashboardShell><RequireDashboardView view={DASHBOARD_VIEWS.EXECUTIVE}><ExecutiveDashboardPage /></RequireDashboardView></DashboardShell>} />
              <Route path="/dashboard/operational" element={<DashboardShell><RequireDashboardView view={DASHBOARD_VIEWS.OPERATIONAL}><OperationalDashboardPage /></RequireDashboardView></DashboardShell>} />
              <Route path="/dashboard/finance" element={<DashboardShell><RequireDashboardView view={DASHBOARD_VIEWS.FINANCE}><FinanceDashboardPage /></RequireDashboardView></DashboardShell>} />
              <Route path="/denials" element={<ServiceFrame serviceUrl={servicePorts.DENIAL_MANAGEMENT} serviceName="Denials & Appeals" />} />
              <Route path="/claims" element={<ServiceFrame serviceUrl={servicePorts.CLAIMS_PROCESSING} serviceName="Claims Processing" servicePath="/claims" />} />
              <Route path="/charges" element={<ServiceFrame serviceUrl={servicePorts.CHARGE_CAPTURE} serviceName="Charge Management" />} />
              <Route path="/medical-codes" element={<ServiceFrame serviceUrl={servicePorts.MEDICAL_CODES} serviceName="Medical Codes" />} />
              <Route path="/ar-posting" element={<ServiceFrame serviceUrl={servicePorts.AR_POSTING} serviceName="AR Posting" />} />
              <Route path="/eob" element={<ServiceFrame serviceUrl={servicePorts.EXPLANATION_OF_BENEFITS} serviceName="Remittance Advice" />} />
              <Route path="/insurance" element={<ServiceFrame serviceUrl={servicePorts.INSURANCE_VERIFICATION} serviceName="Insurance Verification" />} />
              <Route path="/prior-auth" element={<ServiceFrame serviceUrl={servicePorts.PRIOR_AUTHORIZATION} serviceName="Prior Authorization" />} />
              <Route path="/clearinghouse" element={<ServiceFrame serviceUrl={servicePorts.EDI_PLATFORM} serviceName="EDI Platform" />} />
              <Route path="/patient-billing" element={<ServiceFrame serviceUrl={servicePorts.PATIENT_ACCOUNT} serviceName="Patient Billing" />} />
              <Route path="/compliance" element={<ServiceFrame serviceUrl={servicePorts.RCM_COMPLIANCE} serviceName="RCM Compliance" />} />
              <Route path="/agent-studio" element={<ServiceFrame serviceUrl={servicePorts.AI_AGENT_BUILDER} serviceName="AI Agent Builder" />} />
              <Route path="/work-queue" element={<div className="py-6 px-4 sm:px-6 lg:px-8"><WorkQueue /></div>} />
              <Route path="/analytics" element={<div className="py-6 px-4 sm:px-6 lg:px-8"><Analytics /></div>} />
              <Route path="/reporting" element={<div className="h-full"><Reporting /></div>} />
              <Route path="/subscription" element={<div className="py-6 px-4 sm:px-6 lg:px-8"><SubscriptionManagement /></div>} />
              <Route path="/team-queue-goals" element={<Navigate to="/work-queue?tab=team-goals" replace />} />
              <Route path="/team" element={<TeamManagement />} />
              <Route path="/profile" element={<div className="py-6 px-4 sm:px-6 lg:px-8"><Profile /></div>} />
              <Route path="/settings/onboarding" element={<Navigate to="/settings?tab=onboarding" replace />} />
              <Route path="/settings" element={<div className="py-6 px-4 sm:px-6 lg:px-8"><Settings /></div>} />
              <Route path="/patient-portal" element={<PatientPortal />} />
              <Route path="/contact-us" element={<ContactUs />} />
              <Route path="/help" element={<div className="py-6 px-4 sm:px-6 lg:px-8"><Help /></div>} />
              <Route path="/integrations" element={<ServiceFrame serviceUrl={servicePorts.INTEGRATIONS_SERVICE} serviceName="Integrations Service" />} />
              <Route path="/practice-data" element={<ServiceFrame serviceUrl={servicePorts.PRACTICE_DATA_MANAGER} serviceName="Practice Data" />} />
              <Route path="/practice-data/x12-pipeline" element={<Navigate to="/practice-data?module=x12-pipeline" replace />} />
              <Route path="/practice-data/phi-transformer" element={<Navigate to="/practice-data?module=phi-transformer" replace />} />
              <Route path="/x12-pipeline" element={<Navigate to="/practice-data?module=x12-pipeline" replace />} />
              <Route path="/notifications" element={<Notifications />} />
              <Route path="/tasks" element={<div className="py-6 px-4 sm:px-6 lg:px-8"><TaskManager /></div>} />
              <Route path="/activity" element={<ActivityLog />} />
              <Route path="/contract-management" element={<ServiceFrame serviceUrl={servicePorts.CONTRACT_MANAGEMENT} serviceName="Contract Management" />} />
              <Route path="*" element={<Navigate to={DEFAULT_HOME_ROUTE} replace />} />
            </Routes>
          </AccessGuard>
        </main>
        <ProductTourChecklist organizationId={organizationId} />
      </div>
    </div>
    </PlatformKaiHost>
  );
}
